import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  fetchProfile as fetchCustomerProfile,
  updateProfile as updateCustomerProfile,
  fetchConnectorGroups as fetchCustomerConnectorGroups,
  fetchConnectors as fetchCustomerConnectors,
  fetchFileStoreConnection as fetchFileStoreConnection,
  fetchConnectorStatusKpi as fetchConnectorStatusKpis,
  fetchSearchInvoices as fetchSearchInvoicesService,
  listApiTokens as listApiTokensService,
  createApiToken as createApiTokenService,
  updateApiToken as updateApiTokenService,
  deleteApiToken as deleteApiTokenService,
} from 'services/customerService';
import { useAuth } from './useAuth';
import {
  createConnectorGroup as createNewConnectorGroup,
  updateConnectorGroup as updateExistingConnectorGroup,
  deleteConnectorGroup as deleteExistingConnectorGroup,
} from 'services/connectorGroupService';
import {
  createConnector as createNewConnector,
  updateConnector as updateExistingConnector,
  deleteConnector as deleteExistingConnector,
} from 'services/connectorService';

import { ConnectorGroup } from 'models/ConnectorGroup';
import { Connector } from 'models/Connector';
import {
  ApiToken,
  CreateApiTokenRequest,
  MAX_API_TOKENS_PER_USER,
  UpdateApiTokenRequest,
} from 'models/ApiToken';
import { useEntities } from './useEntities';
import {
  calculateAverage,
} from 'utils/summary';

const useCustomer = () => {

  const { accessToken } = useAuth();
  const { connectorGroupsById, connectorsById, upsertConnectorGroups, upsertConnectors } = useEntities();
  const [profile, setProfile] = useState<any>(null);
  const [connectorIds, setConnectorIds] = useState<number[]>([]);
  const [connectorGroupIds, setConnectorGroupIds] = useState<number[]>([]);
  const [fileStoreConnections, setFileStoreConnections] = useState<any[]>([]);
  const [warehouseConnections, setConnectionsWarehouse] = useState<any[]>([]);
  const [efficacyReports, setEfficacyReports] = useState<any[]>([]);
  const [monthlySummary, setMonthlySummary] = useState<any[]>([]);
  const [weeklySummary, setWeeklySummary] = useState<any[]>([]);
  const [connectorReports, setConnectorReports] = useState<any[]>([]);
  const [connectorStatusKpi, setConnectorStatusKpi] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState<boolean>(false);
  const [invoicesLoadingMore, setInvoicesLoadingMore] = useState<boolean>(false);
  const [invoicesHasNextPage, setInvoicesHasNextPage] = useState<boolean>(false);
  const [invoicesNextPageToken, setInvoicesNextPageToken] = useState<string | null>(null);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [apiTokens, setApiTokens] = useState<ApiToken[]>([]);
  const [apiTokensLoading, setApiTokensLoading] = useState<boolean>(false);
  const [apiTokensError, setApiTokensError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!error) return;
    const timeoutId = setTimeout(() => setError(null), 30_000);
    return () => clearTimeout(timeoutId);
  }, [error]);

  const connectorGroups = useMemo(
    () =>
      connectorGroupIds
        .map(id => connectorGroupsById[id])
        .filter((connectorGroup): connectorGroup is ConnectorGroup => Boolean(connectorGroup)),
    [connectorGroupIds, connectorGroupsById]
  );
  const connectors = useMemo(
    () =>
      connectorIds
        .map(id => connectorsById[id])
        .filter((connector): connector is Connector => Boolean(connector)),
    [connectorIds, connectorsById]
  );
  const executeWithLoading = async (asyncFunction: () => Promise<void>) => {

    setLoading(true);
    setError(null);
    try {
      await asyncFunction();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    await executeWithLoading(async () => {
      const profileData = await fetchCustomerProfile(accessToken);
      setProfile(profileData);
    });
  };

  const updateProfile = async (profileData: any) => {
    await executeWithLoading(async () => {
      await updateCustomerProfile(profileData, accessToken);
      const refreshedProfile = await fetchCustomerProfile(accessToken);
      setProfile(refreshedProfile);
    });
  };

  const fetchConnectorGroups = async () => {
    await executeWithLoading(async () => {
      const data = await fetchCustomerConnectorGroups(accessToken);
      upsertConnectorGroups(data);
      setConnectorGroupIds(data.map((connectorGroup: ConnectorGroup) => connectorGroup.id));
    });
  };

  const fetchConnectors = async () => {
    await executeWithLoading(async () => {
      const data = await fetchCustomerConnectors(accessToken);
      upsertConnectors(data);
      setConnectorIds(data.map((connector: Connector) => connector.id));
    });
  };

  const createConnector = async (newConnector: Connector) => {
    await executeWithLoading(async () => {
      await createNewConnector(newConnector, accessToken);
      await fetchConnectors();
    });
  };

  const updateConnector = async (id: number, updatedConnector: Connector) => {
    await executeWithLoading(async () => {
      await updateExistingConnector(id, updatedConnector, accessToken);
      await fetchConnectors();
    });
  };

  const deleteConnector = async (id: number) => {
    await executeWithLoading(async () => {
      await deleteExistingConnector(id, accessToken);
      await fetchConnectors();
    });
  };

  const calculateMonthlyAverage = (monthlySummary: any[], key: string): string => {
    return calculateAverage(monthlySummary, key);
  };


  const createConnectorGroup = async (data: any) => {
    await executeWithLoading(async () => {
      await createNewConnectorGroup(data, accessToken);
      fetchConnectorGroups();
    });
  };

  const updateConnectorGroup = async (id: number, data: any) => {
    await executeWithLoading(async () => {
      await updateExistingConnectorGroup(id, data, accessToken);
      fetchConnectorGroups();
    });
  };

  const deleteConnectorGroup = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      await deleteExistingConnectorGroup(id, accessToken);
      await fetchConnectorGroups();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
        throw err;
      }
      const message = 'An unknown error occurred';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const fetchConnections = async () => {

    await executeWithLoading(async () => {

      const data = await fetchFileStoreConnection(accessToken);
      setFileStoreConnections(Array.isArray(data) ? data : [data]);
    });
  };

  const isAtTokenLimit = apiTokens.length >= MAX_API_TOKENS_PER_USER;

  const fetchApiTokens = useCallback(async () => {
    if (!accessToken) {
      setApiTokens([]);
      setApiTokensLoading(false);
      return;
    }

    setApiTokensLoading(true);
    setApiTokensError(null);
    try {
      const tokens = await listApiTokensService(accessToken);
      setApiTokens(tokens);
    } catch (err: unknown) {
      setApiTokensError(err instanceof Error ? err.message : 'Failed to fetch API tokens');
      setApiTokens([]);
    } finally {
      setApiTokensLoading(false);
    }
  }, [accessToken]);

  const createToken = async (data: CreateApiTokenRequest): Promise<ApiToken> => {
    if (!accessToken) {
      throw new Error('Not authenticated');
    }
    const created = await createApiTokenService(data, accessToken);
    setApiTokens((prev) => [...prev, created]);
    return created;
  };

  const updateToken = async (
    id: number,
    data: UpdateApiTokenRequest,
  ): Promise<ApiToken> => {
    if (!accessToken) {
      throw new Error('Not authenticated');
    }
    const updated = await updateApiTokenService(id, data, accessToken);
    setApiTokens((prev) => prev.map((t) => (t.id === id ? updated : t)));
    return updated;
  };

  const deleteToken = async (id: number): Promise<void> => {
    if (!accessToken) {
      throw new Error('Not authenticated');
    }
    await deleteApiTokenService(id, accessToken);
    setApiTokens((prev) => prev.filter((t) => t.id !== id));
  };

  const fetchInvoices = useCallback(async (startDate: string, endDate: string) => {
    if (!startDate || !endDate || !accessToken) {
      setInvoices([]);
      setInvoicesHasNextPage(false);
      setInvoicesNextPageToken(null);
      setInvoicesLoading(false);
      setInvoicesError(null);
      return;
    }

    setInvoicesLoading(true);
    setInvoicesError(null);
    try {
      const result = await fetchSearchInvoicesService({ startDate, endDate }, accessToken);
      setInvoices(result.invoices);
      setInvoicesHasNextPage(Boolean(result.pagination.has_next_page));
      setInvoicesNextPageToken(result.pagination.next_page_token ?? null);
    } catch (err: unknown) {
      setInvoicesError(err instanceof Error ? err.message : 'Failed to fetch invoices');
      setInvoices([]);
      setInvoicesHasNextPage(false);
      setInvoicesNextPageToken(null);
    } finally {
      setInvoicesLoading(false);
    }
  }, [accessToken]);

  const fetchMoreInvoices = useCallback(async (startDate: string, endDate: string) => {
    if (!startDate || !endDate || !accessToken || !invoicesNextPageToken || invoicesLoadingMore) {
      return;
    }

    setInvoicesLoadingMore(true);
    setInvoicesError(null);
    try {
      const result = await fetchSearchInvoicesService(
        { startDate, endDate, nextPageToken: invoicesNextPageToken },
        accessToken,
      );
      setInvoices(prev => [...prev, ...result.invoices]);
      setInvoicesHasNextPage(Boolean(result.pagination.has_next_page));
      setInvoicesNextPageToken(result.pagination.next_page_token ?? null);
    } catch (err: unknown) {
      setInvoicesError(err instanceof Error ? err.message : 'Failed to fetch invoices');
    } finally {
      setInvoicesLoadingMore(false);
    }
  }, [accessToken, invoicesNextPageToken, invoicesLoadingMore]);



  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      await fetchProfile();
      await fetchConnectorGroups();
      await fetchConnectors();
      await fetchConnections();
    };

    fetchData();
  }, [accessToken]);
  return { profile, updateProfile, connectorGroups, connectors, fileStoreConnections, warehouseConnections, connectorReports, connectorStatusKpi, createConnectorGroup, updateConnectorGroup, deleteConnectorGroup, createConnector, updateConnector, deleteConnector, calculateMonthlyAverage, monthlySummary, efficacyReports, weeklySummary, invoices, fetchInvoices, fetchMoreInvoices, invoicesHasNextPage, invoicesLoading, invoicesLoadingMore, invoicesError, apiTokens, apiTokensLoading, apiTokensError, isAtTokenLimit, tokenCount: apiTokens.length, createToken, updateToken, deleteToken, fetchApiTokens, loading, error };

};

export default useCustomer;
